import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Bug, Lightbulb, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { createFeedbackReport, getMyFeedbackReports } from '@/features/feedback/api';
import { redactRoute } from '@/features/feedback/route';
import {
  FEEDBACK_SEVERITY_LABELS,
  FEEDBACK_STATUS_LABELS,
  type FeedbackKind,
  type FeedbackSeverity,
  type FeedbackStatus,
} from '@/features/feedback/types';

const statusClass: Record<FeedbackStatus, string> = {
  new: 'bg-blue-50 text-blue-800 ring-blue-200',
  triaged: 'bg-purple-50 text-purple-800 ring-purple-200',
  in_progress: 'bg-amber-50 text-amber-900 ring-amber-200',
  shipped: 'bg-green-50 text-green-800 ring-green-200',
  declined: 'bg-gray-100 text-gray-700 ring-gray-200',
};

type Tab = 'new' | 'mine';

export function ReportIssueDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const titleRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<Tab>('new');
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<FeedbackSeverity>('major');

  // Reset to a clean form each time the dialog is opened, so a cancelled report
  // never bleeds into the next one.
  useEffect(() => {
    if (!open) return;
    setTab('new');
    setKind('bug');
    setTitle('');
    setBody('');
    setSeverity('major');
    const focus = window.setTimeout(() => titleRef.current?.focus(), 40);
    return () => window.clearTimeout(focus);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const myReports = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: getMyFeedbackReports,
    enabled: open && tab === 'mine',
  });

  const mutation = useMutation({
    mutationFn: () =>
      createFeedbackReport(
        { kind, title, body, severity: kind === 'bug' ? severity : null },
        redactRoute(location.pathname, location.search),
      ),
    onSuccess: () => {
      toast.success(kind === 'bug' ? 'Bug report sent' : 'Feature request sent');
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!open) return null;

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/40 p-4 sm:items-center">
      <button
        type="button"
        aria-label="Close report dialog"
        className="fixed inset-0 -z-10 cursor-default focus-visible:outline-none"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-dialog-title"
        className="w-full max-w-xl rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="report-dialog-title" className="text-base font-semibold text-gray-900">
              Report a problem or request a feature
            </h2>
            <p className="mt-0.5 text-xs text-gray-600">
              Goes straight to the team. The page you are on is attached automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-5" role="tablist">
          {([['new', 'New report'], ['mine', 'My reports']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`relative -mb-px min-h-11 px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                tab === id
                  ? 'text-blue-800 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'new' ? (
          <form
            className="space-y-4 px-5 py-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <fieldset>
              <legend className="mb-1.5 text-xs font-medium text-gray-700">What is this?</legend>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['bug', 'Something is broken', Bug],
                  ['feature', 'Something is missing', Lightbulb],
                ] as const).map(([value, label, Icon]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={kind === value}
                    onClick={() => setKind(value)}
                    className={`flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                      kind === value
                        ? 'border-blue-300 bg-blue-50 text-blue-900'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">Summary</span>
              <input
                ref={titleRef}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={160}
                required
                placeholder={kind === 'bug' ? 'Export button does nothing on Domain Directory' : 'Let me filter calls by publisher'}
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            {kind === 'bug' && (
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-700">How bad is it?</span>
                <select
                  value={severity}
                  onChange={(event) => setSeverity(event.target.value as FeedbackSeverity)}
                  className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {(Object.keys(FEEDBACK_SEVERITY_LABELS) as FeedbackSeverity[]).map((value) => (
                    <option key={value} value={value}>
                      {FEEDBACK_SEVERITY_LABELS[value]}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-700">
                  {kind === 'bug' ? 'What happened, and what did you expect?' : 'What would you like to be able to do?'}
                </span>
                <textarea
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  rows={5}
                  maxLength={8000}
                  required
                  placeholder={
                    kind === 'bug'
                      ? 'Steps you took, what you saw, and what you expected instead.'
                      : 'What you are trying to get done, and why the current tools do not cover it.'
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Describe the record instead of pasting caller names, phone numbers, or transcripts.
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3">
              <p className="text-xs text-gray-500">Sent from {location.pathname}</p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={!canSubmit}>
                  {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {mutation.isPending ? 'Sending…' : 'Send report'}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="max-h-96 overflow-y-auto px-5 py-4">
            {myReports.isLoading ? (
              <div className="space-y-2" aria-label="Loading your reports">
                {[0, 1, 2].map((value) => (
                  <div key={value} className="h-16 animate-pulse rounded-md bg-gray-100 motion-reduce:animate-none" />
                ))}
              </div>
            ) : myReports.isError ? (
              <div className="py-6 text-center">
                <p className="text-sm text-red-800">{(myReports.error as Error).message}</p>
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void myReports.refetch()}>
                  Try again
                </Button>
              </div>
            ) : (myReports.data ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-600">
                You have not filed anything yet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {(myReports.data ?? []).map((report) => (
                  <li key={report.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{report.title}</p>
                        <p className="mt-0.5 text-xs text-gray-600">
                          {report.kind === 'bug' ? 'Bug' : 'Feature request'} ·{' '}
                          {new Date(report.createdAt).toLocaleDateString()}
                        </p>
                        {report.triageNote && (
                          <p className="mt-1.5 border-l-2 border-gray-300 pl-2 text-xs text-gray-700">
                            {report.triageNote}
                          </p>
                        )}
                      </div>
                      <span
                        className={`inline-flex flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusClass[report.status]}`}
                      >
                        {FEEDBACK_STATUS_LABELS[report.status]}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
